/**
 * AbundanceJourneyPage - The Reclaim Program Experience System
 * 
 * This is the flagship 21-day Abundance Journey with the full 10-step daily structure:
 * 1. Reset (mandatory entry gate)
 * 2. Enter Day button
 * 3. Video
 * 4. Embodiment Exercise (interactive)
 * 5. Regulation Practice (guided)
 * 6. Audio Experience (multi-layer)
 * 7. Reflection (journal input)
 * 8. Intention Lock-In
 * 9. Night Ritual link
 * 10. Morning Prompt (next day)
 */
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { BottomNav } from "../../components/BottomNav";
import { 
  ChevronLeft, Lock, Check, Play, Moon, 
  Sparkles, ChevronRight, Wind, Heart, 
  Eye, Pause, Crown
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Phase colors for visual distinction
const PHASE_COLORS = {
  awakening: { bg: "from-emerald-900/90 to-teal-900/90", accent: "emerald" },
  liberation: { bg: "from-violet-900/90 to-purple-900/90", accent: "violet" },
  expansion: { bg: "from-amber-900/90 to-orange-900/90", accent: "amber" },
  creation: { bg: "from-blue-900/90 to-indigo-900/90", accent: "blue" },
  embodiment: { bg: "from-rose-900/90 to-pink-900/90", accent: "rose" },
};

// Reset type icons and colors
const RESET_CONFIG = {
  breathe: { icon: Wind, label: "Breathe", color: "sky" },
  let_go: { icon: Pause, label: "Let Go", color: "purple" },
  heart: { icon: Heart, label: "Heart", color: "rose" },
  still: { icon: Eye, label: "Stillness", color: "slate" },
};

export const AbundanceJourneyPage = () => {
  const navigate = useNavigate();
  const [journey, setJourney] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchJourney();
  }, []);

  const fetchJourney = async () => {
    const token = localStorage.getItem("reclaim_token");
    if (!token) {
      navigate("/login");
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/journey/abundance`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || "Failed to load journey");
      }

      const data = await response.json();
      setJourney(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleStartDay = (dayNumber) => {
    navigate(`/journey/abundance/day/${dayNumber}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0a0a12] to-[#12121f] flex items-center justify-center">
        <motion.div
          animate={{ opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="text-emerald-400/60 font-heading"
        >
          Loading your journey...
        </motion.div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0a0a12] to-[#12121f] p-6">
        <button 
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-white/60 mb-6"
        >
          <ChevronLeft size={20} />
          Back
        </button>
        <div className="text-center py-12">
          <Lock className="w-16 h-16 text-emerald-400/50 mx-auto mb-4" />
          <h2 className="text-xl font-heading text-white mb-2">Access Required</h2>
          <p className="text-white/60">{error}</p>
        </div>
      </div>
    );
  }

  const progressPercent = journey ? journey.progress.percentage : 0;
  const currentPhase = journey?.days?.find(d => d.is_current)?.phase || "awakening";
  const phaseColors = PHASE_COLORS[currentPhase];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0a0a12] to-[#12121f]">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-gradient-to-b from-[#0a0a12] via-[#0a0a12]/95 to-transparent pb-4">
        <div className="p-4 flex items-center justify-between">
          <button 
            onClick={() => navigate(-1)}
            className="p-2 rounded-full bg-white/5 hover:bg-white/10 transition-colors"
            data-testid="back-button"
          >
            <ChevronLeft size={20} className="text-white" />
          </button>
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-emerald-400" />
            <span className="text-emerald-200/60 text-sm">21 Day Journey</span>
          </div>
        </div>
      </div>

      {/* Journey Header */}
      <div className="px-6 pb-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          {/* Journey Icon */}
          <div className={`w-20 h-20 rounded-full bg-gradient-to-br ${phaseColors.bg}
                        flex items-center justify-center mx-auto mb-4 shadow-lg shadow-emerald-500/20`}>
            <Sparkles className="w-10 h-10 text-white" />
          </div>
          
          <h1 className="text-2xl font-heading text-white mb-2" data-testid="journey-title">
            {journey?.journey?.title}
          </h1>
          <p className="text-white/50 text-sm max-w-xs mx-auto">
            {journey?.journey?.subtitle}
          </p>
        </motion.div>

        {/* Progress Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white/5 rounded-2xl p-5 mb-6 border border-emerald-500/10"
          data-testid="progress-section"
        >
          <div className="flex justify-between items-center mb-3">
            <span className="text-white font-medium">Your Progress</span>
            <span className="text-emerald-400 text-sm">
              {journey?.progress?.completed_days} of {journey?.progress?.total_days} days
            </span>
          </div>
          
          {/* Progress Bar */}
          <div className="h-3 bg-emerald-950/50 rounded-full overflow-hidden mb-4">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${progressPercent}%` }}
              transition={{ duration: 1, ease: "easeOut" }}
              className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full"
            />
          </div>
          
          {/* Day Dots */}
          <div className="flex gap-1 flex-wrap justify-center">
            {journey?.days?.map((day) => (
              <div
                key={day.day_number}
                className={`w-3 h-3 rounded-full transition-all ${
                  day.is_completed 
                    ? "bg-emerald-500" 
                    : day.is_unlocked 
                      ? "bg-emerald-400/50" 
                      : "bg-emerald-950/50"
                }`}
                title={`Day ${day.day_number}: ${day.theme}`}
              />
            ))}
          </div>

          {/* Stats Row */}
          {journey?.progress?.streak_days > 0 && (
            <div className="mt-4 pt-4 border-t border-white/5 flex justify-center gap-6 text-xs">
              <div className="text-center">
                <p className="text-emerald-400 font-bold">{journey.progress.streak_days}</p>
                <p className="text-white/40">Day Streak</p>
              </div>
              <div className="text-center">
                <p className="text-emerald-400 font-bold">{journey.progress.total_reflections}</p>
                <p className="text-white/40">Reflections</p>
              </div>
            </div>
          )}
        </motion.div>

        {/* Premium Upgrade Banner (if not premium) */}
        {!journey?.has_premium && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 rounded-xl p-4 mb-6 border border-amber-500/20"
          >
            <div className="flex items-center gap-3">
              <Crown className="w-8 h-8 text-amber-400" />
              <div className="flex-1">
                <p className="text-white font-medium text-sm">Unlock Full Journey</p>
                <p className="text-amber-200/60 text-xs">Day 1 is free. Upgrade to access all 21 days.</p>
              </div>
              <button 
                onClick={() => navigate("/pricing")}
                className="px-4 py-2 bg-amber-500 text-black text-sm font-semibold rounded-full hover:bg-amber-400 transition-colors"
              >
                Upgrade
              </button>
            </div>
          </motion.div>
        )}

        {/* Phases Section */}
        <div className="space-y-6">
          {/* Group days by phase */}
          {Object.entries(PHASE_COLORS).map(([phase, colors]) => {
            const phaseDays = journey?.days?.filter(d => d.phase === phase) || [];
            if (phaseDays.length === 0) return null;
            
            const firstDay = phaseDays[0];
            const isCurrentPhase = phaseDays.some(d => d.is_current);
            
            return (
              <motion.div
                key={phase}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-3"
              >
                {/* Phase Header */}
                <div className="flex items-center gap-3 px-1">
                  <div className={`w-2 h-2 rounded-full bg-${colors.accent}-500`} />
                  <h2 className="text-white/80 text-sm font-medium uppercase tracking-wider">
                    {firstDay?.phase_label || phase}
                  </h2>
                  {isCurrentPhase && (
                    <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-400 text-xs rounded-full">
                      Current
                    </span>
                  )}
                </div>

                {/* Days in Phase */}
                <div className="space-y-2">
                  {phaseDays.map((day) => (
                    <DayCard 
                      key={day.day_number}
                      day={day}
                      onStart={() => handleStartDay(day.day_number)}
                    />
                  ))}
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Night Integration Link - Updated to Abundance Night Journey */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-8"
        >
          <button
            onClick={() => navigate("/journey/abundance/nights")}
            className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-indigo-500/10 to-purple-500/10 
                     rounded-xl border border-indigo-500/20 hover:border-indigo-400/40 transition-all"
            data-testid="night-integration-link"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-indigo-500/20 flex items-center justify-center">
                <Moon className="w-5 h-5 text-indigo-400" />
              </div>
              <div className="text-left">
                <p className="text-white font-medium text-sm">Abundance Night Journey</p>
                <p className="text-indigo-200/50 text-xs">21 nights of overnight installs — your voice, on a theta bed</p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-indigo-400" />
          </button>
        </motion.div>
      </div>

      {/* Bottom Padding */}
      <div className="h-24" />
      <BottomNav />
    </div>
  );
};


/**
 * Day Card Component
 */
const DayCard = ({ day, onStart }) => {
  const isLocked = !day.is_unlocked;
  const isCompleted = day.is_completed;
  const isCurrent = day.is_current;
  const resetConfig = RESET_CONFIG[day.reset_type] || RESET_CONFIG.breathe;
  const ResetIcon = resetConfig.icon;

  return (
    <motion.div
      whileHover={!isLocked ? { scale: 1.01 } : {}}
      whileTap={!isLocked ? { scale: 0.99 } : {}}
      onClick={() => !isLocked && onStart()}
      className={`
        relative rounded-xl p-4 transition-all cursor-pointer
        ${isLocked 
          ? "bg-white/5 opacity-50 cursor-not-allowed" 
          : isCurrent
            ? "bg-gradient-to-r from-emerald-500/20 to-teal-500/20 border border-emerald-500/30 hover:border-emerald-400/50"
            : isCompleted
              ? "bg-white/5 border border-emerald-500/20"
              : "bg-white/5 hover:bg-white/10"
        }
      `}
      data-testid={`day-card-${day.day_number}`}
    >
      <div className="flex items-center gap-4">
        {/* Day Number */}
        <div className={`
          w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0
          ${isCompleted 
            ? "bg-emerald-500/20" 
            : isCurrent 
              ? "bg-emerald-500/30" 
              : "bg-white/10"
          }
        `}>
          {isCompleted ? (
            <Check className="w-6 h-6 text-emerald-400" />
          ) : isLocked ? (
            <Lock className="w-5 h-5 text-white/30" />
          ) : (
            <span className="text-lg font-bold text-white">{day.day_number}</span>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className={`font-medium ${isLocked ? "text-white/30" : "text-white"}`}>
              Day {day.day_number}
            </h3>
            {isCurrent && (
              <span className="px-2 py-0.5 bg-emerald-500/30 rounded-full text-xs text-emerald-300">
                Current
              </span>
            )}
            {day.requires_premium && isLocked && (
              <Crown className="w-3.5 h-3.5 text-amber-400/50" />
            )}
          </div>
          <p className={`text-sm truncate ${isLocked ? "text-white/20" : "text-white/50"}`}>
            {day.title}
          </p>
          
          {/* Reset Type Indicator */}
          {!isLocked && !isCompleted && (
            <div className="flex items-center gap-1.5 mt-1.5">
              <ResetIcon className={`w-3 h-3 text-${resetConfig.color}-400`} />
              <span className={`text-xs text-${resetConfig.color}-400/70`}>
                {resetConfig.label} Reset
              </span>
            </div>
          )}
          
          {/* Progress Bar for partial completion */}
          {!isLocked && !isCompleted && day.completion_percentage > 0 && (
            <div className="mt-2 h-1 bg-white/10 rounded-full overflow-hidden">
              <div 
                className="h-full bg-emerald-500 rounded-full transition-all"
                style={{ width: `${day.completion_percentage}%` }}
              />
            </div>
          )}
        </div>

        {/* Action */}
        {!isLocked && !isCompleted && (
          <div className="flex-shrink-0">
            <div className="w-10 h-10 rounded-full bg-emerald-500 flex items-center justify-center
                          shadow-lg shadow-emerald-500/30">
              <Play className="w-5 h-5 text-white ml-0.5" />
            </div>
          </div>
        )}

        {isCompleted && (
          <div className="flex-shrink-0 text-emerald-400">
            <Sparkles className="w-5 h-5" />
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default AbundanceJourneyPage;
